/*
Author: Chathuraka Mallawa Arachchi
Date: 18/10/2024
Copyright © 2024 Infix!. All rights reserved.

Responsible for managing frm state including validations.
*/

import {
    IActionItem,
    IAlertItem,
    IAnaeItem,
    ICollapsedSections,
    IHospitalItem,
    IIntendedManagementItem,
    INode,
    IPriorityItem,
    ISelectedInformationPack,
    ISpecialityItem,
    ISurgeonItem,
    ITriageItem,
    IWaitingListRecord
} from '@/components/survey/SurveyModel/types/nodeTypes'
import { PoaAnswerObj } from '@/components/survey/SurveyModel/types/poaTypes'
import { ListTypes, WaitingListRecordStatuses, QuestionnaireResponseStatus } from '@/constants/enums'
import { Connection } from '@/utils'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import download from 'downloadjs'
import { useDispatch } from 'react-redux'

export interface IpoaSurveyState {
    poaQuestionnaire?: INode
    questionnaireDetails?: INode
    answersObj: PoaAnswerObj | null
    alerts: IAlertItem[]
    actions: IActionItem[]
    sectionsWithActions: string[]
    collapsedAll?: boolean
    responseId: number | undefined
    informationPacks: [] | null
    informationPackDetails: [] | null
    selectedInformationPack: ISelectedInformationPack | null
    isLoading: boolean
    selectedTab: string
    sections: ICollapsedSections
    sectionValidations: Record<string, boolean>
    sectionAlerts: Record<string, boolean>
    poaData: any[] /// TODO: Update with a specific type if available
    triageListData: ITriageItem[]
    specialities: ISpecialityItem[] | null
    surgeons: ISurgeonItem[] | null
    poaActions: IActionItem[] | null
    priorities: IPriorityItem[] | null
    anae: IAnaeItem[] | null
    poaTypes: []
    nameChi: [] | null // add relevant interface
    operationProcedures: [] | null //add relevant interface
    intendedManagement: IIntendedManagementItem[] | null
    waitingListRecord: IWaitingListRecord | null
    isLoadingWaitingListRecord: boolean
    isUpdatingPoaStatus: boolean
    hospitals: IHospitalItem[] | null
}

const initialState: IpoaSurveyState = {
    poaQuestionnaire: undefined,
    questionnaireDetails: undefined,
    answersObj: null,
    alerts: [],
    sectionsWithActions: [],
    actions: [],
    collapsedAll: false,
    responseId: undefined,
    informationPacks: [],
    informationPackDetails: [],
    selectedInformationPack: null,
    isLoading: false,
    selectedTab: 'questionnaire',
    sections: {},
    sectionValidations: {},
    sectionAlerts: {},
    poaData: [],
    triageListData: [],
    specialities: [],
    surgeons: [],
    poaActions: [],
    priorities: [],
    anae: [],
    poaTypes: [],
    nameChi: [],
    operationProcedures: [],
    intendedManagement: [],
    waitingListRecord: null,
    isLoadingWaitingListRecord: false,
    isUpdatingPoaStatus: false,
    hospitals: [],
}

const poaSurveySlice = createSlice({
    name: 'poaSurvey',
    initialState,
    reducers: {
        poaSurveyFromDB: (
            state,
            action: PayloadAction<{
                nodes: INode
            }>
        ) => {
            const { nodes } = action.payload
            state.questionnaireDetails = nodes
        },
        setResponseId: (
            state,
            action: PayloadAction<{
                id: number
            }>
        ) => {
            const { id } = action.payload
            state.responseId = id
        },
        poaSurveyState: (
            /* */
            state,
            action: PayloadAction<INode | undefined>
        ) => {
            state.poaQuestionnaire = action.payload
                ? { ...action.payload }
                : undefined
        },
        setQUestionAnswers: (
            state,
            action: PayloadAction<{ answers: PoaAnswerObj }>
        ) => {
            const { answers } = action.payload
            state.answersObj = answers
        },
        reSetQUestionAnswers: (state) => {
            state.answersObj = null
        },
        addAlert: (state, action: PayloadAction<IAlertItem>) => {
            const alert = action.payload
            if (!state.alerts.find(a => a.nodeName === alert.nodeName && a.label === alert.label)) {
                state.alerts.push(alert)
            }
        },
        removeAlert: (state, action: PayloadAction<IAlertItem>) => {
            const alert = action.payload
            state.alerts = state.alerts.filter((a) => (a.nodeName !== alert.nodeName || a.label !== alert.label))
        },
        updateSection: (state, action: PayloadAction<{ sectionName: string; expand?: boolean }>) => {
            const { sectionName, expand } = action.payload
            if (!state.sections) state.sections = {}
            state.sections[sectionName] = {
                isActive: expand !== undefined ? expand : !state.sections[sectionName]?.isActive,
            }
        },
        setSectionInitialState: (state, action: PayloadAction<string>) => {
            const sectionName = action.payload
            if (!state.sections) state.sections = {}
            state.sections[sectionName] = {
                isActive: false,
            }
        },
        updateCollapseAll: (state, action: PayloadAction<boolean>) => {
            const collapseAll = action.payload
            Object.keys(state.sections).forEach((sectionName) => {
                state.sections[sectionName].isActive = collapseAll
            })
        },
        addAction: (state, action: PayloadAction<IActionItem>) => {
            const alertAction = action.payload
            const existingIndex = state.actions.findIndex(
                (action) => action.name === alertAction.name
            )

            if (existingIndex === -1) {
                state.actions.push(alertAction)
            } else {
                // Update the existing action at the found index
                state.actions[existingIndex] = {
                    ...state.actions[existingIndex],
                    ...alertAction, // Overwrite fields from the new payload
                }
            }
        },
        removeAction: (state, action: PayloadAction<string>) => {
            const alertAction = action.payload
            state.actions = state.actions.filter((a) => a.name !== alertAction)
        },
        addSectionWithActions: (state, action: PayloadAction<string>) => {
            const section = action.payload
            if (!state.sectionsWithActions.includes(section)) {
                state.sectionsWithActions.push(section)
            }
        },
        removeSectionWithActions: (state, action: PayloadAction<string>) => {
            const section = action.payload
            state.sectionsWithActions = state.sectionsWithActions.filter(
                (a) => a !== section
            )
        },
        setCollapsedAll: (state, action: PayloadAction<boolean>) => {
            state.collapsedAll = action.payload
        },
        informationPacksFromDB: (
            state,
            action: PayloadAction<{
                data: any
            }>
        ) => {
            const { data } = action.payload
            state.informationPacks = data
        },
        informationPackInformationFromDB: (
            state,
            action: PayloadAction<{
                data: any
            }>
        ) => {
            const { data } = action.payload
            state.informationPackDetails = data
        },
        selectedInformationPack: (
            state,
            action: PayloadAction<{
                data: any
            }>
        ) => {
            const { data } = action.payload
            state.selectedInformationPack = data
        },
        resetPoaSurveyState: () => initialState,
        setIsLoading: (
            state,
            action: PayloadAction<{
                data: boolean
            }>
        ) => {
            const { data } = action.payload
            state.isLoading = data
        },
        setSelectedTab: (state, action: PayloadAction<string>) => {
            state.selectedTab = action.payload
        },
        poaDataFromDB: (
            state,
            action: PayloadAction<{
                data: any
            }>
        ) => {
            const { data } = action.payload
            state.poaData = data
        },
        triageListDataFromDb: (
            state,
            action: PayloadAction<{
                data: any
            }>
        ) => {
            const { data } = action.payload
            state.triageListData = data
        },
        specialitiesFromDb: (
            state,
            action: PayloadAction<{
                data: any
            }>
        ) => {
            const { data } = action.payload
            state.specialities = data
        },
        surgeonsFromDb: (
            state,
            action: PayloadAction<{
                data: any
            }>
        ) => {
            const { data } = action.payload
            state.surgeons = data
        },
        poaActionsFromDb: (
            state,
            action: PayloadAction<{
                data: any
            }>
        ) => {
            const { data } = action.payload
            state.poaActions = data
        },
        prioritiesFromDb: (
            state,
            action: PayloadAction<{
                data: any
            }>
        ) => {
            const { data } = action.payload
            state.priorities = data
        },
        anaeFromDb: (
            state,
            action: PayloadAction<{
                data: any
            }>
        ) => {
            const { data } = action.payload
            state.anae = data
        },
        poaTypesFromDb: (
            state,
            action: PayloadAction<{
                data: any
            }>
        ) => {
            const { data } = action.payload
            state.poaTypes = data
        },
        nameChiOptionsFromDb: (
            state,
            action: PayloadAction<{
                data: any
            }>
        ) => {
            const { data } = action.payload
            state.nameChi = data
        },
        operationProceduresFromDb: (
            state,
            action: PayloadAction<{
                data: any
            }>
        ) => {
            const { data } = action.payload
            state.operationProcedures = data
        },
        fetchIntendedManagement: (
            state,
            action: PayloadAction<{
                data: IIntendedManagementItem[]
            }>
        ) => {
            const { data } = action.payload
            state.intendedManagement = data
        },
        fetchHospitals: (
            state,
            action: PayloadAction<{
                data: IHospitalItem[]
            }>
        ) => {
            const { data } = action.payload
            state.hospitals = data
        },
        updateQuestionnaireResponseId(
            state,
            action: PayloadAction<{
                waitingListRecordId: number;
                questionnaireResponseId: number;
            }>
        ) {
            return {
                ...state,
                triageListData: state.triageListData.map((triageItem: ITriageItem) =>
                    triageItem.id === action.payload.waitingListRecordId
                        ? {
                            ...triageItem,
                            questionnaireResponseId: action.payload.questionnaireResponseId,
                            questionnaireResponseStatusId: QuestionnaireResponseStatus.Sent
                        }
                        : triageItem
                )
            };
        },
        removeQuestionnaire(
            state,
            action: PayloadAction<{
                waitingListRecordId: number;
            }>
        ) {
            return {
                ...state,
                triageListData: state.triageListData.filter((triageItem: ITriageItem) =>triageItem.id !== action.payload.waitingListRecordId)};
        },
        resetSpecialities: (state) => {
            state.specialities = initialState.specialities
        },
        resetSurgeons: (state) => {
            state.surgeons = initialState.surgeons
        },
        resetPoaActions: (state) => {
            state.poaActions = initialState.poaActions
        },
        resetPriorities: (state) => {
            state.priorities = initialState.priorities
        },
        resetAnae: (state) => {
            state.anae = initialState.anae
        },
        resetPoaTypes: (state) => {
            state.poaTypes = initialState.poaTypes
        },
        resetNameChi: (state) => {
            state.nameChi = initialState.nameChi
        },
        resetOperationProcedures: (state) => {
            state.operationProcedures = initialState.operationProcedures
        },
        resetIntendedManagement: (state) => {
            state.intendedManagement = initialState.intendedManagement
        },
        setSectionValidation: (
            state,
            action: PayloadAction<{ sectionName: string; isValid: boolean }>
        ) => {
            const { sectionName, isValid } = action.payload
            state.sectionValidations[sectionName] = isValid
        },
        resetSectionValidations: (state) => {
            state.sectionValidations = initialState.sectionValidations
        },
        setSectionAlert: (
            state,
            action: PayloadAction<{ sectionName: string; hasAlert: boolean }>
        ) => {
            const { sectionName, hasAlert } = action.payload
            state.sectionAlerts[sectionName] = hasAlert
        },
        resetSectionAlerts: (state) => {
            state.sectionAlerts = initialState.sectionAlerts
        },
        setWaitingListRecord: (
            state,
            action: PayloadAction<IWaitingListRecord | null>
        ) => {
            state.waitingListRecord = action.payload
        },
        setIsLoadingWaitingListRecord: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.isLoadingWaitingListRecord = action.payload
        },
        setIsLoadingUpdatePoaStatus: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.isUpdatingPoaStatus = action.payload
        },
        updateWaitingListRecord: (
            state,
            action: PayloadAction<Partial<IWaitingListRecord>>
        ) => {
            if (state.waitingListRecord) {
                state.waitingListRecord = {
                    ...state.waitingListRecord,
                    ...action.payload,
                }
            }
        },
        resetHospitals: (state) => {
            state.hospitals = initialState.hospitals
        },

        updatePoaDateTime(
            state,
            action: PayloadAction<{
                waitingListRecordId: number;
                poaDateTime: string;
            }>
        ) {
            state.poaData = state.poaData.map((poaItem: any) =>
                poaItem.id === action.payload.waitingListRecordId
                    ? {
                        ...poaItem,
                        questionnaireResponsePOADateTime: action.payload.poaDateTime
                    }
                    : poaItem
            );
        },
    },
})

export const {
    poaSurveyState,
    poaSurveyFromDB,
    setQUestionAnswers,
    addAlert,
    removeAlert,
    setResponseId,
    reSetQUestionAnswers,
    informationPacksFromDB,
    informationPackInformationFromDB,
    selectedInformationPack,
    resetPoaSurveyState,
    setIsLoading,
    setSelectedTab,
    updateSection,
    updateCollapseAll,
    removeAction,
    addSectionWithActions,
    addAction,
    removeSectionWithActions,
    setSectionInitialState,
    poaDataFromDB,
    specialitiesFromDb,
    surgeonsFromDb,
    poaActionsFromDb,
    prioritiesFromDb,
    anaeFromDb,
    poaTypesFromDb,
    nameChiOptionsFromDb,
    operationProceduresFromDb,
    triageListDataFromDb,
    updateQuestionnaireResponseId,
    removeQuestionnaire,
    resetSpecialities,
    resetSurgeons,
    resetPoaActions,
    resetPriorities,
    resetAnae,
    resetPoaTypes,
    resetNameChi,
    resetOperationProcedures,
    fetchIntendedManagement,
    resetIntendedManagement,
    fetchHospitals,
    resetHospitals,
    updatePoaDateTime,
    setSectionValidation,
    resetSectionValidations,
    setSectionAlert,
    resetSectionAlerts,
    setWaitingListRecord,
    setIsLoadingWaitingListRecord,
    setIsLoadingUpdatePoaStatus,
    updateWaitingListRecord,
} = poaSurveySlice.actions

export const useGetInformationPacks = () => {
    const dispatch = useDispatch()

    const getInformationPacks = async () => {
        try {
            dispatch(setIsLoading({ data: true }))
            const url = Connection.baseUrl('informationPacks')
            const response = await fetch(url, {
                method: 'GET',
                headers: Connection.headers(),
            })

            if (!response.ok)
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )

            const data = await response.json()
            dispatch(informationPacksFromDB({ data }))
            dispatch(setIsLoading({ data: false }))
        } catch (error) {
            console.error('Failed to fetch Information Packs Data:', error)
            dispatch(setIsLoading({ data: false }))
        }
    }
    return getInformationPacks
}

export const useGetInformationPacksDetails = () => {
    const dispatch = useDispatch()

    const updateInformationPackDetails = async (informationPackId: number) => {
        try {
            dispatch(setIsLoading({ data: true }))
            const url =
                Connection.baseUrl('informationPackDetails') +
                `/${informationPackId}`
            const response = await fetch(url, {
                method: 'GET',
                headers: Connection.headers(),
            })

            if (!response.ok)
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )
            const data = await response.json()
            dispatch(informationPackInformationFromDB({ data }))
            dispatch(setIsLoading({ data: false }))
        } catch (error) {
            console.error('Failed to Get Information Pack data:', error)
            dispatch(setIsLoading({ data: false }))
        }
    }
    return updateInformationPackDetails
}

export const useDownloadInformationPackFile = () => {
    const downloadInformationPackFile = async (
        fileId: number,
        informationPackId: number,
        fileName: string
    ) => {
        try {
            const url =
                Connection.baseUrl('informationPacks') +
                `/${informationPackId}/` +
                `files/${fileId}`
            const response = await fetch(url, {
                method: 'GET',
                headers: Connection.headers(),
            })

            if (!response.ok)
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )
            const data = await response.json()
            try {
                if (data.fileUrl) {
                    const fileResponse = await fetch(data.fileUrl)
                    const blob = await fileResponse.blob()
                    download(blob, fileName)
                }
            } catch (error) {
                console.error('Failed to Get Information Pack data:', error)
            }
        } catch (error) {
            console.error('Failed to Get Information Pack data:', error)
        }
    }
    return downloadInformationPackFile
}

export const useGetInformationPackForWaitingListRecord = () => {
    const dispatch = useDispatch()

    const getInformationPackForWaitingListRecord = async (
        waitingListRecordId: string
    ) => {
        try {
            const url =
                Connection.baseUrl('WaitingList') + `/${waitingListRecordId}`
            const response = await fetch(url, {
                method: 'GET',
                headers: Connection.headers(),
            })

            if (!response.ok)
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )
            const data = await response.json()
            dispatch(selectedInformationPack({ data }))
        } catch (error) {
            console.error('Failed to Get Information Pack data:', error)
        }
    }
    return getInformationPackForWaitingListRecord
}

export const useGetPoaSurvey = () => {
    const dispatch = useDispatch()

    const getQuestionnaire = async (id: number) => {
        try {
            dispatch(setIsLoading({ data: true }))
            // Call your API to get the answers
            const response = await fetch(
                Connection.baseUrl('getQuestionnaire') +
                    `?id=${id}&definitionTypeId=1`, // 2 is patient 1 poa
                {
                    method: 'GET',
                    headers: Connection.headers(),
                }
            )

            if (!response.ok) {
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )
            }
            const definition = await response.json()
            const nodes = definition.questionnaireDefinition.definitionJson
            const sections = nodes.nodes.map((node: INode) => node.name)
            if (sections && sections.length > 0) {
                sections.reduce(
                    (
                        acc: Record<string, { isActive: boolean }>,
                        section: string
                    ) => ({ ...acc, [section]: { isActive: false } }),
                    {} as Record<string, { isActive: boolean }>
                )
                sections.forEach((section: string) => {
                    dispatch(setSectionInitialState(section))
                })
            }
            dispatch(poaSurveyFromDB({ nodes }))
            dispatch(setIsLoading({ data: false }))
        } catch (error) {
            dispatch(setIsLoading({ data: false }))
            console.error('Failed to fetch question answers:', error)
        }
    }

    return getQuestionnaire
}

export const useSetSurveyState = () => {
    const dispatch = useDispatch()

    const setSurveyState = (value: INode) => {
        dispatch(poaSurveyState(value))
    }

    return setSurveyState
}

export const useSetResponseId = () => {
    const dispatch = useDispatch()

    const setAnswerResponseId = (id: number) => {
        dispatch(setResponseId({ id }))
    }

    return setAnswerResponseId
}

export const useGetQUestionAnswers = () => {
    const dispatch = useDispatch()
    const getQuestionnaireAnswers = async (
        questionnaireResponseId: any,
        ptient: boolean = false
    ) => {
        try {
            // Call your API to get the answers
            const response = await fetch(
                Connection.baseUrl(`QuestionnaireAnswers`) +
                    `?questionnaireResponseId=${questionnaireResponseId}&onlyPatientAnswers=${ptient}`,
                {
                    method: 'GET',
                    headers: Connection.headers(),
                }
            )
            if (!response.ok) {
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )
            }
            const answers = await response.json()
            // Dispatch the answers to Redux
            dispatch(setQUestionAnswers({ answers }))
        } catch (error) {
            console.error('Failed to fetch question answers:', error)
        }
    }

    return getQuestionnaireAnswers
}

export const useResetGetQUestionAnswers = () => {
    const dispatch = useDispatch()

    const resetQuestionnaireAnswers = () => {
        dispatch(reSetQUestionAnswers())
    }

    return resetQuestionnaireAnswers
}

export const useUpdateAlerts = () => {
    const dispatch = useDispatch()
    const addNewAlert = (nodeName: string, newAlert: string) => {
        dispatch(addAlert({nodeName, label: newAlert}))
    }

    const removeAlertByName = (nodeName: string, alertLabel: string) => {
        dispatch(removeAlert({nodeName, label: alertLabel}))
    }
    return { addNewAlert, removeAlertByName }
}

export const useUpdateActions = () => {
    const dispatch = useDispatch()
    const addNewAction = (newAction: IActionItem) => {
        dispatch(addAction(newAction))
    }

    const removeActionByName = (newAction: string) => {
        dispatch(removeAction(newAction))
    }
    return { addNewAction, removeActionByName }
}

export const useUpdateSectionsWithActions = () => {
    const dispatch = useDispatch()
    const addSectionWithActionsByName = (newAction: string) => {
        dispatch(addSectionWithActions(newAction))
    }

    const removeSectionWithActionsByName = (newAction: string) => {
        dispatch(removeSectionWithActions(newAction))
    }
    return { addSectionWithActionsByName, removeSectionWithActionsByName }
}
export const useResetPoaSurveyState = () => {
    const dispatch = useDispatch()

    const resetPoaSurvey = () => {
        dispatch(resetPoaSurveyState())
    }

    return resetPoaSurvey
}

export const useTabSelect = () => {
    const dispatch = useDispatch()

    const selectTab = (tab: string) => {
        dispatch(setSelectedTab(tab))
    }

    return selectTab
}

export const useUpdateAccordionSection = () => {
    const dispatch = useDispatch()
    const updateAccordionSection = (sectionName: string, expand?: boolean) => {
        dispatch(updateSection({ sectionName, expand }))
    }
    return updateAccordionSection
}

export const useUpdateCollapsedAll = () => {
    const dispatch = useDispatch()
    const updateCollapsedAll = (collapsedAll: boolean) => {
        dispatch(updateCollapseAll(collapsedAll))
    }
    return updateCollapsedAll
}

export const useGetPoaListData = () => {
    const dispatch = useDispatch()

    const getPoaListListData = async (
        specialities?: number[],
        surgeons?: number[],
        actions?: string[],
        priorityIds?: number[],
        operationProcedureIds?: number[],
        anaestheticTypeIds?: number[],
        poaTypes?: number[],
        questionnaireStatusIds?: (number | null)[],
        waitingListRecordStatusIds?: number[],
        CHIs?: string[],
        poaStartDate?: Date,
        poaEndDate?: Date,
        operationListStartDate?: Date,
        operationListEndDate?: Date,
        referenceCodeIds?: number[],
        hospitalIds?: number[],
        teamReviewOutcomes?: string[],
        expirationDateStartDate?: Date,
        expirationDateEndDate?: Date,
        sortByColumn?: string | null,
        sortOrder?: string | null
    ) => {
        try {
            const url = Connection.baseUrl('WaitingList')
            const filteringParams: any = {}

            if (specialities?.length) {
                filteringParams.SpecialityIds = specialities
            }

            if (surgeons?.length) {
                filteringParams.SurgeonIds = surgeons
            }

            if (actions?.length) {
                filteringParams.Actions = actions
            }

            if (priorityIds?.length) {
                filteringParams.PriorityIds = priorityIds
            }

            if (operationProcedureIds?.length) {
                filteringParams.OperationProcedureIds = operationProcedureIds
            }

            if (anaestheticTypeIds?.length) {
                filteringParams.AnaestheticTypeIds = anaestheticTypeIds
            }

            if (poaTypes?.length) {
                filteringParams.POATypes = poaTypes
            }

            if (waitingListRecordStatusIds?.length) {
                filteringParams.WaitingListRecordStatusIds =
                    waitingListRecordStatusIds
            }

            if (CHIs?.length) {
                filteringParams.CHIs = CHIs
            }
            if (questionnaireStatusIds?.length) {
                // Status ids plus optional null: backend treats null as "no active questionnaire response".
                filteringParams.QuestionnaireStatusIds = questionnaireStatusIds
            }

            filteringParams.POAStartDate = poaStartDate
            filteringParams.POAEndDate = poaEndDate
            filteringParams.OperationDateStartDate = operationListStartDate
            filteringParams.OperationDateEndDate = operationListEndDate
            if (referenceCodeIds?.length) {
                filteringParams.ReferenceCodeIds = referenceCodeIds
            }
            if (hospitalIds?.length) {
                filteringParams.HospitalIds = hospitalIds
            }
            if (teamReviewOutcomes?.length) {
                filteringParams.TeamReviewOutcomes = teamReviewOutcomes
            }
            if (expirationDateStartDate) {
                filteringParams.ExpirationDateStartDate = expirationDateStartDate
            }
            if (expirationDateEndDate) {
                filteringParams.ExpirationDateEndDate = expirationDateEndDate
            }
            dispatch(poaDataFromDB({ data: [] }))

            const response = await fetch(url, {
                method: 'POST',
                headers: Connection.headers(),
                body: JSON.stringify({
                    FilteringParams: filteringParams,
                    SortingParams:
                        sortByColumn && sortOrder
                            ? [sortByColumn.concat(' ', sortOrder)]
                            : ["Priority asc"],
                }),
            })

            if (!response.ok)
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )

            const data = await response.json()
            dispatch(poaDataFromDB({ data }))
        } catch (error) {
            console.error('Failed to fetch triage data:', error)
        }
    }
    return getPoaListListData
}

export const useGetSpecialities = () => {
    const dispatch = useDispatch()

    const getSpecialities = async (type: ListTypes) => {
        try {
            let queryParams = ''

            switch (type) {
                case ListTypes.TriageList:
                    queryParams = '?wlrStatuses=1'
                    break
                case ListTypes.PoaList:
                    queryParams = '?wlrStatuses=2&wlrStatuses=3&wlrStatuses=4'
                    break
                default:
                    queryParams = ''
            }

            const url = Connection.baseUrl('specialities') + queryParams

            const response = await fetch(url, {
                method: 'GET',
                headers: Connection.headers(),
            })

            if (!response.ok)
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )

            const data = await response.json()
            dispatch(specialitiesFromDb({ data }))
        } catch (error) {
            console.error('', error)
        }
    }
    return getSpecialities
}

export const useGetSurgeons = () => {
    const dispatch = useDispatch()

    const getSurgeons = async (type: ListTypes) => {
        try {
            let queryParams = ''
            switch (type) {
                case ListTypes.TriageList:
                    queryParams = '?wlrStatuses=1'
                    break
                case ListTypes.PoaList:
                    queryParams = '?wlrStatuses=2&wlrStatuses=3&wlrStatuses=4'
                    break
                default:
                    queryParams = ''
            }
            const url = Connection.baseUrl('surgeons') + queryParams
            const response = await fetch(url, {
                method: 'GET',
                headers: Connection.headers(),
            })

            if (!response.ok)
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )

            const data = await response.json()
            dispatch(surgeonsFromDb({ data }))
        } catch (error) {
            console.error('', error)
        }
    }
    return getSurgeons
}

export const useGetPoaActions = () => {
    const dispatch = useDispatch()

    const getPoaActions = async () => {
        try {
            const url = Connection.baseUrl('poaActions')
            const response = await fetch(url, {
                method: 'GET',
                headers: Connection.headers(),
            })

            if (!response.ok)
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )

            const data = await response.json()
            dispatch(poaActionsFromDb({ data }))
        } catch (error) {
            console.error('', error)
        }
    }
    return getPoaActions
}

export const useGetPriorities = () => {
    const dispatch = useDispatch()

    const getPriorities = async (type: ListTypes) => {
        try {
            let queryParams = ''

            switch (type) {
                case ListTypes.TriageList:
                    queryParams = '?wlrStatuses=1'
                    break
                case ListTypes.PoaList:
                    queryParams = '?wlrStatuses=2&wlrStatuses=3&wlrStatuses=4'
                    break
                default:
                    queryParams = ''
            }
            const url = Connection.baseUrl('priorities') + queryParams
            const response = await fetch(url, {
                method: 'GET',
                headers: Connection.headers(),
            })

            if (!response.ok)
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )

            const data = await response.json()
            dispatch(prioritiesFromDb({ data }))
        } catch (error) {
            console.error('', error)
        }
    }
    return getPriorities
}

export const useGetAnae = () => {
    const dispatch = useDispatch()

    const getAnae = async (type: ListTypes) => {
        try {
            let queryParams = ''

            switch (type) {
                case ListTypes.TriageList:
                    queryParams = '?wlrStatuses=1'
                    break
                case ListTypes.PoaList:
                    queryParams = '?wlrStatuses=2&wlrStatuses=3&wlrStatuses=4'
                    break
                default:
                    queryParams = ''
            }
            const url = Connection.baseUrl('anaestheticTypes') + queryParams
            const response = await fetch(url, {
                method: 'GET',
                headers: Connection.headers(),
            })

            if (!response.ok)
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )

            const data = await response.json()
            dispatch(anaeFromDb({ data }))
        } catch (error) {
            console.error('', error)
        }
    }
    return getAnae
}

export const useGetNameChiOptions = () => {
    const dispatch = useDispatch()
    const getNameChiOptions = async (chiNumber: string, type: ListTypes) => {
        try {
            const filteringParams: any = {}
            filteringParams.CHIs = [chiNumber]
            switch (type) {
                case ListTypes.TriageList:
                    filteringParams.waitingListRecordStatusIds = [
                        WaitingListRecordStatuses.Initial,
                    ]
                    break
                case ListTypes.PoaList:
                    filteringParams.waitingListRecordStatusIds = [
                        WaitingListRecordStatuses.POA,
                        WaitingListRecordStatuses.Expired,
                        WaitingListRecordStatuses.Complete,
                    ]
                    break
                default:
            }

            const url = Connection.baseUrl('WaitingList')

            const response = await fetch(url, {
                method: 'POST',
                headers: Connection.headers(),
                body: JSON.stringify({
                    FilteringParams: filteringParams,
                }),
            })

            if (!response.ok)
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )
            const data = await response.json()

            if (data.length > 0) {
                const chiOptions = data.map((item: any) => ({
                    value: item.chiNumber,
                    label: `${item.forename} ${item.surname} - ${item.chiNumber}`,
                }))
                dispatch(nameChiOptionsFromDb({ data: chiOptions }))
            }
        } catch (error) {
            console.error('', error)
        }
    }
    return getNameChiOptions
}

export const useGetNameOpcs4OperationsOptions = () => {
    const dispatch = useDispatch()

    const getNameOpcs4OperationsOptions = async (
        type: ListTypes,
        opcsSerachtext: string
    ) => {
        try {
            let queryParams = ''

            switch (type) {
                case ListTypes.TriageList:
                    queryParams = `?wlrStatuses=1&searchText=${opcsSerachtext}`
                    break
                case ListTypes.PoaList:
                    queryParams = `?wlrStatuses=2&wlrStatuses=3&wlrStatuses=4&searchText=${opcsSerachtext}`
                    break
                default:
                    queryParams = ''
            }

            const url = Connection.baseUrl('operationProcedures') + queryParams
            const response = await fetch(url, {
                method: 'GET',
                headers: Connection.headers(),
            })

            if (!response.ok)
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )

            const data = await response.json()
            dispatch(operationProceduresFromDb({ data }))
        } catch (error) {
            console.error('', error)
        }
    }
    return getNameOpcs4OperationsOptions
}

export const useGetTriageListData = () => {
    const dispatch = useDispatch()

    const getTriageListData = async (
        specialities?: number[],
        surgeons?: number[],
        priorityIds?: number[],
        CHIs?: string[],
        operationProcedureIds?: number[],
        anaestheticTypeIds?: number[],
        waitingListRecordStatusIds?: number[],
        questionnaireStatusId?: number[],
        requested?: boolean[],
        sortByColumn?: string | null,
        sortOrder?: string | null,
        operationStartDate?: Date,
        operationEndDate?: Date,
        intendedManagementIds?: number[],
        hospitalIds?: number[]
    ) => {
        try {
            const url = Connection.baseUrl('WaitingList')
            const filteringParams: any = {}
            filteringParams.WaitingListRecordStatusIds = ['1']

            if (specialities?.length) {
                filteringParams.SpecialityIds = specialities
            }

            if (surgeons?.length) {
                filteringParams.SurgeonIds = surgeons
            }

            if (priorityIds?.length) {
                filteringParams.PriorityIds = priorityIds
            }

            if (CHIs?.length) {
                filteringParams.CHIs = CHIs
            }

            if (operationProcedureIds?.length) {
                filteringParams.OperationProcedureIds = operationProcedureIds
            }

            if (anaestheticTypeIds?.length) {
                filteringParams.AnaestheticTypeIds = anaestheticTypeIds
            }

            if (waitingListRecordStatusIds?.length) {
                filteringParams.WaitingListRecordStatusIds =
                    waitingListRecordStatusIds
            }

            if (questionnaireStatusId?.length) {
                filteringParams.QuestionnaireStatusIds = questionnaireStatusId
            }

            if (requested?.length) {
                filteringParams.requestedValues = requested
            }

            if (operationStartDate) {
                filteringParams.operationDateStartDate = operationStartDate
            }

            if (operationEndDate) {
                filteringParams.operationDateEndDate = operationEndDate
            }

            if (intendedManagementIds?.length) {
                filteringParams.referenceCodeIds = intendedManagementIds
            }

            if (hospitalIds?.length) {
                filteringParams.HospitalIds = hospitalIds
            }

            dispatch(triageListDataFromDb({ data: [] }))

            const response = await fetch(url, {
                method: 'POST',
                headers: Connection.headers(),
                body: JSON.stringify({
                    FilteringParams: filteringParams,
                    SortingParams:
                        sortByColumn && sortOrder
                            ? [sortByColumn.concat(' ', sortOrder)]
                            : ['Priority asc"'],
                }),
            })

            if (!response.ok)
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )

            const data = await response.json()
            dispatch(triageListDataFromDb({ data }))
        } catch (error) {
            console.error('Failed to fetch triage data:', error)
        }
    }
    return getTriageListData
}

export const useUpdateInitialPoaType = () => {
    const dispatch = useDispatch();
    const updateInitialPoaStatusDispatch = async (waitingListRecordId: number) => {
        try {
            const response = await fetch(
                Connection.baseUrl('waitingListRecords') + `/${waitingListRecordId}/questionnaireresponses`,
                {
                    method: 'POST',
                    headers: Connection.headers(),
                    body: JSON.stringify({}),
                }
            )
            if (!response.ok) {
                throw new Error(`Error: ${response.status} - ${response.statusText}`)
            }
            const data = await response.json()
            dispatch(updateQuestionnaireResponseId({ waitingListRecordId: waitingListRecordId, questionnaireResponseId: data }))
        } catch (error) {
            console.error('Failed to update waiting list status:', error)
        }
    }
    return updateInitialPoaStatusDispatch
}

export const useUpdatePoaType = () => {
    const dispatch = useDispatch();
    const updatePoaStatusDispatch = async (waitingListRecordId: number,id: number, payload: any, listType?: ListTypes) => {
        try {
            dispatch(setIsLoadingUpdatePoaStatus(true))
            const response = await fetch(
                Connection.baseUrl('waitingListRecords') + `/${waitingListRecordId}/questionnaireresponses/${id}`,
                {
                    method: 'PATCH',
                    headers: Connection.headers(),
                    body: JSON.stringify(payload),
                }
            )

            if (!response.ok) {
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )
            }
            if(listType === ListTypes.TriageList){
                dispatch(removeQuestionnaire({ waitingListRecordId: waitingListRecordId}))
            }

            if(listType === ListTypes.PoaList){
                const poaDateTimeUpdate = payload.find((item: any) => item.path === "/POADateTime");
                if (poaDateTimeUpdate) {
                    dispatch(updatePoaDateTime({ 
                        waitingListRecordId: waitingListRecordId, 
                        poaDateTime: poaDateTimeUpdate.value.toISOString() 
                    }));
                }
            }

            dispatch(setIsLoadingUpdatePoaStatus(false))
        } catch (error) {
            console.error('Failed to fetch question answers:', error)
            dispatch(setIsLoadingUpdatePoaStatus(false))
        }
    }

    return updatePoaStatusDispatch
}

export const useMoveToPoa = () => {
    const dispatch = useDispatch();
    const moveToPoa = async (
        waitingListRecordId: number,
        statusId: number,
        poaType: number,
        poaDateTime: string | null,
        bookedInPMS: boolean
    ) => {
        try {
            let payload: any = {
                waitingListRecordId,
                bookedInPMS,
            }
            if (bookedInPMS) {
                payload = {
                    ...payload,
                    statusId,
                    poaType,
                    poaDateTime,
                }
            }
            const response = await fetch(
                Connection.baseUrl('waitingListRecords') + `/${waitingListRecordId}/questionnaireresponses`,
                {
                    method: 'POST',
                    headers: Connection.headers(),
                    body: JSON.stringify(payload),
                }
            )

            if (!response.ok) {
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )
            }

            const data = await response.json()
            dispatch(removeQuestionnaire({ waitingListRecordId }))
            return data
        } catch (error) {
            console.error('Failed to move record to POA:', error)
        }
    }
    return moveToPoa
}

export const useResetSpecialities = () => {
    const dispatch = useDispatch()
    return () => dispatch(resetSpecialities())
}

export const useResetSurgeons = () => {
    const dispatch = useDispatch()
    return () => dispatch(resetSurgeons())
}

export const useResetPoaActions = () => {
    const dispatch = useDispatch()
    return () => dispatch(resetPoaActions())
}

export const useResetPriorities = () => {
    const dispatch = useDispatch()
    return () => dispatch(resetPriorities())
}

export const useResetAnae = () => {
    const dispatch = useDispatch()
    return () => dispatch(resetAnae())
}

export const useResetPoaTypes = () => {
    const dispatch = useDispatch()
    return () => dispatch(resetPoaTypes())
}

export const useResetNameChi = () => {
    const dispatch = useDispatch()
    return () => dispatch(resetNameChi())
}

export const useResetOperationProcedures = () => {
    const dispatch = useDispatch()
    return () => dispatch(resetOperationProcedures())
}

export const useGetIntendedManagement = () => {
    const dispatch = useDispatch()

    const getIntendedManagement = async () => {
        try {
            const url = Connection.baseUrl('intendedManagement')
            const response = await fetch(url, {
                method: 'GET',
                headers: Connection.headers(),
            })

            if (!response.ok)
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )

            const data: IIntendedManagementItem[] = await response.json()
            dispatch(fetchIntendedManagement({ data }))
        } catch (error) {
            console.error('Failed to fetch intended management options:', error)
        }
    }
    return getIntendedManagement
}

export const useGetHospitals = () => {
    const dispatch = useDispatch()

    const getHospitals = async () => {
        try {
            const url = Connection.baseUrl('hospitals')
            const response = await fetch(url, {
                method: 'GET',
                headers: Connection.headers(),
            })

            if (!response.ok)
                throw new Error(
                    `Error: ${response.status} - ${response.statusText}`
                )

            const data: IHospitalItem[] = await response.json()
            dispatch(fetchHospitals({ data }))
        } catch (error) {
            console.error('Failed to fetch hospitals:', error)
        }
    }
    return getHospitals
}

export const useResetIntendedManagement = () => {
    const dispatch = useDispatch()
    return () => dispatch(resetIntendedManagement())
}

export const useUpdateSectionValidation = () => {
    const dispatch = useDispatch()
    const setSectionValidationStatus = (sectionName: string, isValid: boolean) => {
        dispatch(setSectionValidation({ sectionName, isValid }))
    }
    return setSectionValidationStatus
}

export const useResetSectionValidations = () => {
    const dispatch = useDispatch()
    return () => dispatch(resetSectionValidations())
}

export const useUpdateSectionAlert = () => {
    const dispatch = useDispatch()
    const setSectionAlertStatus = (sectionName: string, hasAlert: boolean) => {
        dispatch(setSectionAlert({ sectionName, hasAlert }))
    }
    return setSectionAlertStatus
}

export const useResetSectionAlerts = () => {
    const dispatch = useDispatch()
    return () => dispatch(resetSectionAlerts())
}

export const useResetHospitals = () => {
    const dispatch = useDispatch()
    return () => dispatch(resetHospitals())
}

export const useResetAllPoaSurveyLists = () => {
    const resetSpecialities = useResetSpecialities()
    const resetSurgeons = useResetSurgeons()
    const resetPoaActions = useResetPoaActions()
    const resetPriorities = useResetPriorities()
    const resetAnae = useResetAnae()
    const resetPoaTypes = useResetPoaTypes()
    const resetNameChi = useResetNameChi()
    const resetOperationProcedures = useResetOperationProcedures()
    const resetIntendedManagement = useResetIntendedManagement()
    const resetHospitals = useResetHospitals()

    return () => {
        resetSpecialities()
        resetSurgeons()
        resetPoaActions()
        resetPriorities()
        resetAnae()
        resetPoaTypes()
        resetNameChi()
        resetOperationProcedures()
        resetIntendedManagement()
        resetHospitals()
    }
}

export const useGetWaitingListRecord = () => {
    const dispatch = useDispatch()

    const getWaitingListRecord = async (id: number): Promise<IWaitingListRecord | null> => {
        try {
            dispatch(setIsLoadingWaitingListRecord(true))
            const url = Connection.baseUrl('WaitingListRecord').replace('{id}', id.toString())
            const response = await fetch(url, {
                method: 'GET',
                headers: Connection.headers(),
            })

            if (!response.ok)
                throw new Error(`Error: ${response.status} - ${response.statusText}`)
            const data: IWaitingListRecord = await response.json()
            dispatch(setWaitingListRecord(data))
            dispatch(setIsLoadingWaitingListRecord(false))
            return data
        } catch (error) {
            console.error('Failed to fetch waiting list record:', error)
            dispatch(setWaitingListRecord(null))
            dispatch(setIsLoadingWaitingListRecord(false))
            return null
        }
    }
    return getWaitingListRecord
}

export default poaSurveySlice.reducer
